/**
 * LERA Gallery Overflow + Stage Nav — Sprint 8.4A.1
 * Fixed 4-thumb overflow + premium +N card + desktop arrows.
 * Navigation reuses LeraGalleryNav / media-gallery.setActiveMedia.
 */

(function () {
  const DESKTOP_MQL = '(min-width: 750px)';
  const SLOT_HIDDEN_CLASS = 'lera-gallery-overflow--slot-hidden';
  const ACTIVE_CLASS = 'lera-product-gallery--overflow-active';
  const DEFAULT_VISIBLE = 4;
  const THUMB_SELECTOR = '.lera-gallery-thumb';
  const boundGalleries = new WeakSet();

  function readConfig(gallery) {
    const productInfo = gallery.closest('product-info');
    const node = productInfo?.querySelector('[data-lera-gallery-overflow-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  function getEligibleThumbs(thumbList) {
    if (!thumbList) return [];
    return Array.from(
      thumbList.querySelectorAll(`${THUMB_SELECTOR}:not(.lera-media-sync--hidden):not([hidden])`)
    );
  }

  function getVisibleCount(config) {
    const override = Number(config?.maxVisibleOverride);
    if (Number.isFinite(override) && override > 0) return Math.max(1, Math.floor(override));
    return DEFAULT_VISIBLE;
  }

  function getThumbPreviewSrc(thumb) {
    const img = thumb?.querySelector('img');
    if (!img) return '';
    return img.currentSrc || img.src || img.getAttribute('src') || '';
  }

  class LeraGalleryOverflowController {
    constructor(gallery) {
      this.gallery = gallery;
      this.config = readConfig(gallery);
      this.mql = window.matchMedia(DESKTOP_MQL);
      this.stage = gallery.querySelector('.lera-gallery-layout__stage');
      this.thumbList = gallery.querySelector('.lera-gallery-thumbs__list');
      this.overflowCard = gallery.querySelector('[data-lera-gallery-overflow]');
      this.overflowButton = this.overflowCard?.querySelector('button');
      this.overflowLabel = this.overflowCard?.querySelector('.lera-gallery-overflow-card__label');
      this.overflowBg = this.overflowCard?.querySelector('[data-lera-gallery-overflow-bg]');
      this.navRoot = gallery.querySelector('[data-lera-gallery-nav]');
      this.prevButton = this.navRoot?.querySelector('[data-lera-gallery-nav-prev]');
      this.nextButton = this.navRoot?.querySelector('[data-lera-gallery-nav-next]');
      this.state = { regularVisible: 0, overflowCount: 0 };
      this.recalcFrame = null;
      this.thumbObserver = null;
    }

    bind() {
      if (!this.config || !this.stage || !this.thumbList) return;
      if (boundGalleries.has(this.gallery)) return;
      boundGalleries.add(this.gallery);

      this.applyThemeVars();

      if (this.config.enabled && this.overflowButton) {
        this.overflowButton.addEventListener('click', this.onOverflowClick.bind(this));
      }

      if (this.config.arrowsEnabled && this.prevButton && this.nextButton) {
        this.prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          window.LeraGalleryNav?.prev(this.gallery);
        });
        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          window.LeraGalleryNav?.next(this.gallery);
        });
      }

      this.mql.addEventListener('change', () => this.scheduleRecalculate());
      window.addEventListener('resize', () => this.scheduleRecalculate(), { passive: true });
      document.addEventListener('lera:variant:selected', this.onVariantChange.bind(this));
      document.addEventListener('variant:selected', this.onVariantChange.bind(this));
      this.gallery.addEventListener('lera:gallery:navigate', () => this.updateArrowState());

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = event?.data?.sectionId;
          if (sectionId && String(sectionId) !== String(this.config.sectionId)) return;
          this.scheduleRecalculate();
        });
      }

      this.thumbObserver = new MutationObserver(() => this.scheduleRecalculate());
      this.thumbObserver.observe(this.thumbList, {
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'aria-hidden'],
        subtree: true,
      });

      const viewerList = this.gallery.querySelector('.product__media-list');
      if (viewerList) {
        this.slideObserver = new MutationObserver(() => this.updateArrowState());
        this.slideObserver.observe(viewerList, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
          childList: true,
        });
      }

      customElements.whenDefined('media-gallery').then(() => this.scheduleRecalculate());
      this.scheduleRecalculate();
    }

    applyThemeVars() {
      const root = this.gallery;
      root.style.setProperty('--lera-go-bg-opacity', String((this.config.bgOpacity ?? 55) / 100));
      root.style.setProperty('--lera-go-text-size', `${this.config.textSize ?? 18}px`);
      root.style.setProperty('--lera-go-radius', `${this.config.radius ?? 4}px`);
      root.style.setProperty('--lera-go-font-weight', String(this.config.fontWeight ?? 700));
      root.style.setProperty('--lera-go-color', this.config.color ?? '#ffffff');

      root.style.setProperty('--lera-go-arrow-size', `${this.config.arrowSize ?? 44}px`);
      root.style.setProperty('--lera-go-arrow-bg', this.config.arrowBackground ?? '#ffffff');
      root.style.setProperty('--lera-go-arrow-radius', `${this.config.arrowRadius ?? 50}%`);
      root.style.setProperty('--lera-go-arrow-shadow', this.config.arrowShadow || '0 6px 18px rgba(0,0,0,0.2)');
      root.style.setProperty('--lera-go-arrow-opacity', String((this.config.arrowOpacity ?? 60) / 100));
      root.style.setProperty('--lera-go-arrow-hover-opacity', String((this.config.arrowHoverOpacity ?? 100) / 100));
    }

    onVariantChange(event) {
      const productInfo = this.gallery.closest('product-info');
      const productId = productInfo?.dataset?.productId;
      const detailProductId = event?.detail?.product?.id;
      if (detailProductId && productId && String(detailProductId) !== String(productId)) return;
      this.scheduleRecalculate();
    }

    scheduleRecalculate() {
      if (this.recalcFrame) cancelAnimationFrame(this.recalcFrame);
      this.recalcFrame = requestAnimationFrame(() => {
        this.recalcFrame = null;
        this.recalculate();
      });
    }

    resetOverflow() {
      this.gallery.classList.remove(ACTIVE_CLASS);
      this.thumbList?.querySelectorAll(`.${SLOT_HIDDEN_CLASS}`).forEach((node) => {
        node.classList.remove(SLOT_HIDDEN_CLASS);
      });
      if (this.overflowCard) this.overflowCard.hidden = true;
      if (this.overflowBg) {
        this.overflowBg.removeAttribute('src');
        this.overflowBg.removeAttribute('srcset');
      }
      this.state = { regularVisible: 0, overflowCount: 0 };
    }

    recalculate() {
      this.updateArrowsVisibility();

      if (!this.config.enabled || !this.overflowCard || !this.overflowButton) {
        this.resetOverflow();
        this.updateArrowState();
        return;
      }

      const thumbs = getEligibleThumbs(this.thumbList);
      const visibleCap = getVisibleCount(this.config);

      if (thumbs.length <= visibleCap) {
        this.resetOverflow();
        this.updateArrowState();
        return;
      }

      const regularVisible = visibleCap;
      const overflowCount = thumbs.length - regularVisible;
      this.applyOverflow(thumbs, regularVisible, overflowCount);
      this.updateArrowState();
    }

    applyOverflow(thumbs, regularVisible, overflowCount) {
      this.gallery.classList.add(ACTIVE_CLASS);

      thumbs.forEach((thumb, index) => {
        thumb.classList.toggle(SLOT_HIDDEN_CLASS, index >= regularVisible);
      });

      this.positionOverflowCard(thumbs, regularVisible);
      this.updateOverflowVisual(thumbs[regularVisible], overflowCount);
      this.state = { regularVisible, overflowCount };
    }

    positionOverflowCard(thumbs, regularVisible) {
      const card = this.overflowCard;
      card.hidden = false;

      const anchor = regularVisible > 0 ? thumbs[regularVisible - 1] : null;
      if (anchor) {
        if (card.previousElementSibling !== anchor) {
          anchor.insertAdjacentElement('afterend', card);
        }
      } else if (this.thumbList.firstElementChild !== card) {
        this.thumbList.prepend(card);
      }
    }

    updateOverflowVisual(firstHiddenThumb, overflowCount) {
      const label = `+${overflowCount}`;
      if (this.overflowLabel) this.overflowLabel.textContent = label;

      this.overflowButton.setAttribute(
        'aria-label',
        `View ${overflowCount} more product image${overflowCount === 1 ? '' : 's'}`
      );

      if (!this.overflowBg || !firstHiddenThumb) return;

      const sourceImg = firstHiddenThumb.querySelector('img');
      const src = getThumbPreviewSrc(firstHiddenThumb);
      if (!src) return;

      this.overflowBg.src = src;
      if (sourceImg?.srcset) this.overflowBg.srcset = sourceImg.srcset;
      else this.overflowBg.removeAttribute('srcset');
      if (sourceImg?.sizes) this.overflowBg.sizes = sourceImg.sizes;
    }

    updateArrowsVisibility() {
      if (!this.navRoot) return;

      const keys = window.LeraGalleryNav?.getMediaKeys(this.gallery) || [];
      const show = Boolean(this.config.arrowsEnabled && this.mql.matches && keys.length > 1);
      this.navRoot.hidden = !show;
      this.gallery.classList.toggle('lera-product-gallery--has-arrows', show);
    }

    updateArrowState() {
      this.updateArrowsVisibility();
      if (!this.prevButton || !this.nextButton) return;

      const keys = window.LeraGalleryNav?.getMediaKeys(this.gallery) || [];
      const disabled = keys.length <= 1;
      this.prevButton.disabled = disabled;
      this.nextButton.disabled = disabled;
    }

    onOverflowClick(event) {
      event.preventDefault();

      const thumbs = getEligibleThumbs(this.thumbList);
      const firstHidden = thumbs[this.state.regularVisible];
      if (!firstHidden) return;

      const hiddenTargets = thumbs.slice(this.state.regularVisible).map((thumb) => thumb.dataset.target);
      const navigableKeys = window.LeraGalleryNav?.getMediaKeys(this.gallery) || [];
      const targetKey =
        hiddenTargets.find((key) => navigableKeys.includes(key)) || firstHidden.dataset.target;

      this.gallery.dispatchEvent(
        new CustomEvent('lera:gallery:overflow:open', {
          bubbles: true,
          detail: {
            sectionId: this.config.sectionId,
            targetKey,
            hiddenCount: this.state.overflowCount,
            hiddenTargets,
            firstHiddenIndex: this.state.regularVisible,
          },
        })
      );

      if (window.LeraPremiumGalleryViewer?.open) {
        window.LeraPremiumGalleryViewer.open({
          gallery: this.gallery,
          targetKey,
          hiddenTargets,
          hiddenCount: this.state.overflowCount,
        });
        return;
      }

      window.LeraGalleryNav?.goTo(this.gallery, targetKey);
    }
  }

  function boot() {
    document.querySelectorAll('media-gallery.lera-product-gallery').forEach((gallery) => {
      new LeraGalleryOverflowController(gallery).bind();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  window.LeraGalleryOverflow = {
    LeraGalleryOverflowController,
    getEligibleThumbs,
    DEFAULT_VISIBLE,
  };
})();
